import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Forces the browser to scroll to the top on every route change so mobile users
 * don't inherit the previous page's scroll position after navigation.
 *
 * Cooperates with the auth redirect-back scroll restore (lib/authRedirect.js):
 *  - Skips when the URL has a hash fragment (deep-link anchors take priority).
 *  - Skips when only the search string or hash changes on the same pathname
 *    (avoids interrupting Forum tab-switch / filter interactions).
 *  - Skips when the current pathname matches the saved return-to path — that
 *    scenario is handled by `redirectAfterAuth` which restores exact scrollY
 *    with its own retry loop.
 *
 * Rendered once inside <BrowserRouter> in App.js; produces no DOM.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    // Skip when an auth return-to is queued for this exact page — redirectAfterAuth
    // will run its own precise scroll restore right after the route mounts.
    try {
      const raw = window.sessionStorage.getItem("k12_return_to");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.path && parsed.path.split("?")[0].split("#")[0] === pathname) return;
      }
    } catch {}
    // Use "auto" (instant) instead of smooth — mobile users expect immediate reset.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}
